import { defineStore } from "pinia";
import ChallengeFormsApi from "@/api/Api/ChallengeFormsApi";
import { useAdminStore } from "@/store/adminStore";
import RebuttalsApi from "@/api/Api/RebuttalsApi";
import { useUserStore } from "@/store/user";
import MessagesApi from "@/api/Api/MessagesApi";
import { useYearStore } from "@/store/yearStore";
import Vue from "vue";
import Urls from "@/urls";
import { useRouter } from "vue-router/composables";

export const useChallengesStore = defineStore({
  id: "challengeStore",
  state: () => ({
    challengeForms: [],
    openChallengeForm: {
      supportingDocs: [],
      challengesAttributes: [{}, {}, {}],
      messages: [],
    },
    openRebuttalForm: { supportingDocs: [] },
    loading: false,
  }),
  getters: {
    getChallengeForms() {
      return this.challengeForms;
    },
    getOpenChallengeForm() {
      return this.openChallengeForm;
    },
  },
  actions: {
    async fetchChallenges(yearIdToFetch) {
      const yearId =
        yearIdToFetch || useAdminStore().adminYear.id || useYearStore().year.id;
      this.loading = true;
      return ChallengeFormsApi.list({ yearId })
        .then((response) => {
          this.challengeForms = response;
        })
        .finally(() => (this.loading = false));
    },
    async setOpenChallengeForm(id, championshipTeamId) {
      if (this.challengeForms.length === 0) await this.fetchChallenges();
      const form = this.challengeForms.find(
        (form) =>
          form.id === id && form.championshipTeamId === championshipTeamId
      );
      if (form) {
        this.openChallengeForm = form;
      }
    },
    async setNewRebuttalForm(yearId, submissionId, challengeFormId) {
      let openChallengeForm;
      try {
        openChallengeForm = await RebuttalsApi.new({
          yearId,
          championshipTeamId: submissionId,
          challengeFormId,
        });
      } catch (error) {
        if (error.response) {
          const { status } = error.response;

          if (status === 401) {
            // Unauthorized, redirect to login
            sessionStorage.setItem("last_url_tennis", window.location.href);
            location.assign(Urls.login);
          } else if (status === 403) {
            // Forbidden, navigate to "forbidden" route
            const router = useRouter();
            router.push({ name: "forbidden" });
          }
        }
      }
      if (openChallengeForm) this.openChallengeForm = openChallengeForm;
      const userStore = useUserStore();
      if (openChallengeForm.rebuttalsAttributes.length > 0) {
        this.openRebuttalForm = openChallengeForm.rebuttalsAttributes[0];
      } else {
        this.openRebuttalForm.name = userStore.user.name;
        this.openRebuttalForm.institution = userStore.user.orgName;
        this.openRebuttalForm.email = userStore.user.email;
        this.openRebuttalForm.declinedRebuttal = false;
        this.openRebuttalForm.rebuttalResponse = "";
        this.openRebuttalForm.supportingDocs = [];
        this.openRebuttalForm.uploadedSupportingDocs = [];
        this.openRebuttalForm.id = null;
      }
    },
    async setNewChallengeForm() {
      const userStore = useUserStore();
      this.openChallengeForm = {
        name: userStore.user.name,
        institution: userStore.user.orgName,
        email: userStore.user.email,
        supportingDocs: [],
        challengesAttributes: [
          { originalRanking: "" },
          { originalRanking: "" },
          { originalRanking: "" },
        ],
      };
    },

    addBlankRebuttalToChallengeForm() {
      this.openChallengeForm.rebuttalsAttributes.push({
        name: "",
        institution: "",
        email: "",
        cellPhone: "",
        rebuttalResponse: "",
        uploadedSupportingDocs: [],
        supportingDocs: [],
      });
    },

    async createMessage(message) {
      const yearStore = useYearStore();
      // update the message in openChallengeForm.messages even before saving it, if the saving/creating fails then undo the pushing of the new message
      const newMessage = await MessagesApi.create({
        params: {
          yearId: yearStore.year.id,
          championshipTeamId: this.openChallengeForm.championshipTeamId,
          challengeFormId: this.openChallengeForm.id,
        },
        data: { challenge_message: message },
      });
      this.openChallengeForm.messages.push(newMessage);
    },
    async updateMessage(message) {
      const yearStore = useYearStore();
      await MessagesApi.update({
        params: {
          yearId: yearStore.year.id,
          championshipTeamId: this.openChallengeForm.championshipTeamId,
          challengeFormId: this.openChallengeForm.id,
          id: message.id,
        },
        data: { challenge_message: message },
      });
      // Find the index of the updated message in openChallengeForm.messages
      const messageIndex = this.openChallengeForm.messages.findIndex(
        (m) => m.id === message.id
      );

      // If the message was found, update it in the store
      if (messageIndex !== -1) {
        Vue.set(this.openChallengeForm.messages, messageIndex, message);
      }
    },
  },
});
